/**
 * CombatAnimationView - Manages ordered consumption of combat events for frontend animation.
 *
 * Responsibilities:
 * - Consume backend combat event stream (CombatAttackEvent, CombatDamageEvent,
 *   CombatDeathEvent, CombatKillGoldEvent, CombatAbilityEvent) in strict order
 * - Ensure event N cannot visually resolve before event N-1
 * - Provide a queue-based interface for the animation layer to consume events one at a time
 * - Track which events have been consumed to prevent out-of-order rendering
 * - Support event replay for debugging/replay scenarios
 *
 * The backend is authoritative; the animation view only renders what the server
 * sends and in the exact order the server sends it.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import {
  ServerToClientMessage,
  CombatAttackEvent,
  CombatDamageEvent,
  CombatDeathEvent,
  CombatKillGoldEvent,
  CombatAbilityEvent,
  CombatStarted,
  CombatResult,
  CombatStream,
  CombatStreamEvent,
} from '../../shared/messages/MessageContracts';
import { COMBAT_STREAM_START_DELAY_MS, getCombatEventDelayMs, isSimultaneousStreamEvent } from '../../shared/CombatAnimationTiming';

// ─── Animation Event Types ──────────────────────────────────────────────────

/**
 * Types of combat events that can be animated.
 */
export type AnimatedCombatEventType =
  | 'attack'
  | 'damage'
  | 'death'
  | 'killGold'
  | 'ability'
  | 'explodingShimmer'
  | 'aura'
  | 'necromancy'
  | 'infestation'  // Infestation (task 194): a corpse is replaced in place by a Fragile Swarmer that attacks immediately (task 204: renamed from 'spawner' with the removal of the accidental Spawner class)
  | 'treasure'     // Treasure Hunter (task 196): a corpse is replaced in place by a temporary treasure token
  | 'shieldBroken' // Shield Breaker (Warrior/Blightcaster, level 3): the target's Shield is gone for the rest of the combat (red shield-shatter burst + badge drop)
  | 'suddenDeath'
  | 'toxicCloud'
  | 'mark'
  | 'sharedPain'
  | 'freeze'
  | 'freezeSkip'
  | 'lifeBurst'    // LifeBurst (Healer, task 206): the board-wide heal flash (both sides' minions)
  | 'inspired'     // Inspired (temporary keyword, task 207): the Hero's kill grants +2/+2 (doubled per re-grant) to friendly minions
  | 'swarm'
  | 'spellCast'
  | 'combatStarted'
  | 'combatResult';

/**
 * A combat event wrapped with ordering metadata for animation.
 */
export interface AnimatedCombatEvent {
  /** Sequential index from the backend (0-based, monotonically increasing). */
  sequenceIndex: number;

  /** The type of animation event. */
  eventType: AnimatedCombatEventType;

  /** The raw server message. */
  message: ServerToClientMessage;

  /** Whether this event has been consumed by the animation layer. */
  consumed: boolean;

  /** Timestamp when this event was received. */
  receivedAt: number;
}

/**
 * Render data for the combat animation view.
 */
export interface CombatAnimationViewRenderData {
  /** Whether the animation view is active (combat in progress). */
  isActive: boolean;

  /** Queue of events waiting to be animated. */
  pendingEvents: AnimatedCombatEvent[];

  /** Events that have been consumed (animated). */
  consumedEvents: AnimatedCombatEvent[];

  /** Total number of events received. */
  totalEventCount: number;

  /** Number of events consumed. */
  consumedCount: number;

  /** Number of events pending. */
  pendingCount: number;

  /** Whether all events have been consumed. */
  allConsumed: boolean;

  /** Current animation sequence index (next event to consume). */
  nextSequenceIndex: number | null;

  /** Whether combat has ended. */
  combatEnded: boolean;
}

/**
 * Callback invoked when new events arrive or are consumed.
 */
export type CombatAnimationViewRenderCallback = (data: CombatAnimationViewRenderData) => void;

/**
 * Callback invoked when a new event is added to the queue.
 */
export type NewEventCallback = (event: AnimatedCombatEvent) => void;

// ─── Combat Animation View ──────────────────────────────────────────────────

export class CombatAnimationView {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: CombatAnimationViewRenderCallback[] = [];
  private _newEventCallbacks: NewEventCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  // Internal event tracking
  private _eventQueue: AnimatedCombatEvent[] = [];
  private _combatActive: boolean = false;
  private _combatEnded: boolean = false;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data. */
  get renderData(): CombatAnimationViewRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the animation view is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns true if combat is active and events are being collected. */
  get isActive(): boolean {
    return this._combatActive;
  }

  /** Returns true if combat has ended. */
  get combatHasEnded(): boolean {
    return this._combatEnded;
  }

  /** Returns the number of pending events waiting to be consumed. */
  get pendingCount(): number {
    return this._eventQueue.filter((e) => !e.consumed).length;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the animation view and start listening for combat events.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    this._notifyRenderCallbacks();
  }

  /**
   * Hide the animation view and stop listening for events.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Reset all animation state (called when combat ends or phase changes).
   * @param clearQueue If true, clear the event queue. If false, keep pending
   * events so animations can complete. Defaults to true for full reset.
   */
  reset(clearQueue: boolean = true): void {
    if (clearQueue) {
      this._eventQueue = [];
    }
    this._combatActive = false;
    this._combatEnded = false;
    this._lastProcessedStreamMatchId = null;
    this._lastProcessedStreamEventCount = 0;
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: CombatAnimationViewRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to new event arrival notifications.
   * Returns an unsubscribe function.
   */
  onNewEvent(callback: NewEventCallback): () => void {
    this._newEventCallbacks.push(callback);
    return () => {
      const index = this._newEventCallbacks.indexOf(callback);
      if (index !== -1) {
        this._newEventCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Event Consumption API ───────────────────────────────────────────────

  /**
   * Consume the next pending event in order.
   * Returns the event if available, null if queue is empty.
   *
   * Events can only be consumed in strict sequence order. Attack A cannot
   * visually resolve after later event B — the consumer must take events
   * from the front of the queue.
   */
  consumeNextEvent(): AnimatedCombatEvent | null {
    const nextIndex = this._getNextPendingIndex();
    if (nextIndex === -1) {
      return null;
    }

    const event = this._eventQueue[nextIndex];
    event.consumed = true;

    this._notifyRenderCallbacks();
    return event;
  }

  /**
   * Peek at the next pending event without consuming it.
   * Returns null if queue is empty.
   */
  peekNextEvent(): AnimatedCombatEvent | null {
    const nextIndex = this._getNextPendingIndex();
    if (nextIndex === -1) {
      return null;
    }
    return this._eventQueue[nextIndex];
  }

  /**
   * Consume all pending events at once (for fast-forward/skip).
   * Returns the consumed events.
   */
  consumeAllEvents(): AnimatedCombatEvent[] {
    const consumed: AnimatedCombatEvent[] = [];
    let event;
    while ((event = this.consumeNextEvent()) !== null) {
      consumed.push(event);
    }
    return consumed;
  }

  /**
   * Get all events (consumed and pending) for replay/debugging.
   */
  getAllEvents(): AnimatedCombatEvent[] {
    return [...this._eventQueue];
  }

  /**
   * Get events by type filter.
   */
  getEventsByType(eventType: AnimatedCombatEventType): AnimatedCombatEvent[] {
    return this._eventQueue.filter((e) => e.eventType === eventType);
  }

  // ─── Internal Event Handling ─────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        const state = event.data as { match: any };
        const matchState = state.match;

        // Quick exit: skip combat event processing on non-combat state changes
        // (timer ticks, HUD updates, phase changes that aren't combat-related).
        // The isCombatEvent flag is set by GameStateService only when combat
        // event data actually changed.
        const hasCombatData = matchState.combatStarted || matchState.combatStream
          || matchState.lastCombatAttack || matchState.lastCombatDamage
          || matchState.lastCombatDeath || matchState.lastCombatKillGold
          || matchState.lastCombatAbility || matchState.lastCombatResult;
        if (!matchState.isCombatEvent && !hasCombatData) {
          // Non-combat state change with no pending combat data — skip entirely.
          // Reset when returning to preparation (full) or game over (keep queue).
          if (matchState.phase === 'preparation' && !matchState.combatStarted) {
            this.reset(true);
          }
          if (matchState.gameOver) {
            this.reset(false);
          }
          return;
        }

        // Detect combat start
        if (matchState.combatStarted && !this._combatActive) {
          this._combatActive = true;
          this._combatEnded = false;

          // Record CombatStarted as the first animation event
          this._addEvent(matchState.combatStarted, 'combatStarted');
        }

        // Process batched combat stream (new approach — all events in one message)
        if (matchState.combatStream && this._combatActive) {
          const stream = matchState.combatStream;
          const streamKey = `${stream.matchId}-${stream.events.length}`;
          if (streamKey !== `${this._lastProcessedStreamMatchId}-${this._lastProcessedStreamEventCount}`) {
            this._lastProcessedStreamMatchId = stream.matchId;
            this._lastProcessedStreamEventCount = stream.events.length;
            this._processCombatStream(stream);
          }
        }

        // Process combat attack events
        if (matchState.lastCombatAttack && this._combatActive) {
          const attackKey = `${matchState.lastCombatAttack.attackerInstanceId}-${matchState.lastCombatAttack.defenderInstanceId}`;
          if (attackKey !== this._lastProcessedAttackKey) {
            this._lastProcessedAttackKey = attackKey;
            this._addEvent(matchState.lastCombatAttack, 'attack');
          }
        }

        // Process combat damage events (data key + counter to handle repeated attacks)
        if (matchState.lastCombatDamage && this._combatActive) {
          const dmg = matchState.lastCombatDamage;
          const dataKey = `${dmg.attackerInstanceId}-${dmg.defenderInstanceId}-${dmg.attackerHpAfter}-${dmg.defenderHpAfter}`;
          if (dataKey !== this._lastProcessedDamageKey) {
            this._lastProcessedDamageKey = dataKey;
            this._lastProcessedDamageSeq++;
            this._addEvent(matchState.lastCombatDamage, 'damage');
          }
        }

        // Process combat death events
        if (matchState.lastCombatDeath && this._combatActive) {
          const deathKey = `${matchState.lastCombatDeath.minionInstanceId}-${matchState.lastCombatDeath.playerId}`;
          if (deathKey !== this._lastProcessedDeathKey) {
            this._lastProcessedDeathKey = deathKey;
            this._addEvent(matchState.lastCombatDeath, 'death');
          }
        }

        // Process combat kill gold events
        if (matchState.lastCombatKillGold && this._combatActive) {
          const goldKey = `${matchState.lastCombatKillGold.killerInstanceId}-${matchState.lastCombatKillGold.victimInstanceId}-${matchState.lastCombatKillGold.goldAwarded}`;
          if (goldKey !== this._lastProcessedGoldKey) {
            this._lastProcessedGoldKey = goldKey;
            this._addEvent(matchState.lastCombatKillGold, 'killGold');
          }
        }

        // Process combat ability events
        if (matchState.lastCombatAbility && this._combatActive) {
          const abilityKey = `${matchState.lastCombatAbility.abilityId}-${matchState.lastCombatAbility.sourceInstanceId}-${matchState.lastCombatAbility.triggerType}`;
          if (abilityKey !== this._lastProcessedAbilityKey) {
            this._lastProcessedAbilityKey = abilityKey;
            this._addEvent(matchState.lastCombatAbility, 'ability');
          }
        }

        // Process combat result
        // Skip synchronous _combatEnded when using batched CombatStream —
        // the CombatResult event in the stream sets it via setTimeout callback
        // so animations have time to play before the combat area is hidden.
        if (matchState.lastCombatResult && this._combatActive && !matchState.combatStream) {
          const resultKey = `${matchState.lastCombatResult.matchId}-${matchState.lastCombatResult.winnerId ?? 'null'}`;
          if (resultKey !== this._lastProcessedResultKey) {
            this._lastProcessedResultKey = resultKey;
            this._addEvent(matchState.lastCombatResult, 'combatResult');
            this._combatEnded = true;
          }
        }

        // Reset when phase returns to preparation (no active combat) — full reset.
        if (matchState.phase === 'preparation' && !matchState.combatStarted) {
          this.reset(true);
        }
        // On game over, reset flags but keep event queue so animations complete.
        if (matchState.gameOver) {
          this.reset(false);
        }

        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }


  /**
   * Processes a batched CombatStream, scheduling events with local timing delays
   * so animations play at a natural pace rather than all at once.
   *
   * Optimization: Batches render callbacks so that all events scheduled within
   * a 100ms window trigger a single render notification instead of one per event.
   * This reduces the number of full re-render cycles during combat.
   */
  private _processCombatStream(stream: CombatStream): void {
    // Intro beat: the combat board (rendered from CombatStarted) gets
    // COMBAT_STREAM_START_DELAY_MS on screen before the FIRST event plays —
    // on a slow page the opening attacks would otherwise fire before the
    // board has rendered and be missed. The delay applies once, before the
    // first event only (never between events), and is included in the
    // shared calculateCombatStreamDurationMs so the server's post-combat
    // wait stays in sync with this playback.
    let cumulativeDelay = COMBAT_STREAM_START_DELAY_MS;
    let prevSimultaneous = false;
    let batchNotifyTimeout: ReturnType<typeof setTimeout> | null = null;
    let pendingNotify = false;

    for (const event of stream.events) {
      // Shared timing table — the server uses the same delays to decide when
      // it may send the next round's state without cutting off animations.
      // A simultaneous event that follows another simultaneous event (e.g., an
      // Explosive hitting several minions at once) fires at the SAME instant
      // as the previous one, so all of its damage/deaths play out together.
      const isSim = isSimultaneousStreamEvent(event);
      if (!(isSim && prevSimultaneous)) {
        cumulativeDelay += getCombatEventDelayMs(event);
      }
      prevSimultaneous = isSim;

      setTimeout(() => {
        const animatedEvent = this._streamEventToAnimatedEvent(event);
        if (animatedEvent) {
          this._eventQueue.push(animatedEvent);
          for (const callback of this._newEventCallbacks) {
            callback(animatedEvent);
          }
        }

        // Check if combat ended
        if (event.messageType === 'CombatResult') {
          this._combatEnded = true;
        }

        // Batch render notifications: coalesce multiple events within 100ms
        // into a single render callback to avoid thrashing the UI.
        pendingNotify = true;
        if (!batchNotifyTimeout) {
          batchNotifyTimeout = setTimeout(() => {
            batchNotifyTimeout = null;
            if (pendingNotify) {
              pendingNotify = false;
              this._notifyRenderCallbacks();
            }
          }, 100);
        }
      }, cumulativeDelay);
    }

    // Ensure final notification fires after the last event
    setTimeout(() => {
      if (batchNotifyTimeout) {
        clearTimeout(batchNotifyTimeout);
        batchNotifyTimeout = null;
      }
      if (pendingNotify) {
        pendingNotify = false;
        this._notifyRenderCallbacks();
      }
    }, cumulativeDelay + 50);
  }

  /**
   * Converts a CombatStreamEvent to an AnimatedCombatEvent.
   */
  private _streamEventToAnimatedEvent(event: CombatStreamEvent): AnimatedCombatEvent | null {
    const eventTypeMap: Record<string, AnimatedCombatEventType> = {
      CombatAttackEvent: 'attack',
      CombatDamageEvent: 'damage',
      CombatDeathEvent: 'death',
      CombatKillGoldEvent: 'killGold',
      CombatAbilityEvent: 'ability',
      CombatExplodingShimmerEvent: 'explodingShimmer',
      CombatAuraEvent: 'aura',
      CombatNecromancyEvent: 'necromancy',
      CombatInfestationEvent: 'infestation',
      CombatTreasureEvent: 'treasure',
      CombatShieldBrokenEvent: 'shieldBroken',
      CombatLifeBurstEvent: 'lifeBurst',
      CombatInspiredEvent: 'inspired',
      CombatSuddenDeathEvent: 'suddenDeath',
      CombatToxicCloudEvent: 'toxicCloud',
      CombatMarkEvent: 'mark',
      CombatSharedPainEvent: 'sharedPain',
      CombatFreezeEvent: 'freeze',
      CombatFreezeSkipEvent: 'freezeSkip',
      CombatSwarmEvent: 'swarm',
      CombatSpellCastEvent: 'spellCast',
      CombatResult: 'combatResult',
    };

    const eventType = eventTypeMap[event.messageType];
    if (!eventType) return null;

    return {
      sequenceIndex: this._eventQueue.length,
      eventType,
      message: event as unknown as ServerToClientMessage,
      consumed: false,
      receivedAt: Date.now(),
    };
  }

  private _addEvent(message: ServerToClientMessage, eventType: AnimatedCombatEventType): void {
    const animatedEvent: AnimatedCombatEvent = {
      sequenceIndex: this._eventQueue.length,
      eventType,
      message,
      consumed: false,
      receivedAt: Date.now(),
    };

    this._eventQueue.push(animatedEvent);

    // Notify new event callbacks
    for (const callback of this._newEventCallbacks) {
      callback(animatedEvent);
    }
  }

  private _getNextPendingIndex(): number {
    for (let i = 0; i < this._eventQueue.length; i++) {
      if (!this._eventQueue[i].consumed) {
        return i;
      }
    }
    return -1;
  }

  // ─── Render Data Construction ────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _buildRenderData(): CombatAnimationViewRenderData {
    const pendingEvents = this._eventQueue.filter((e) => !e.consumed);
    const consumedEvents = this._eventQueue.filter((e) => e.consumed);
    const nextIndex = this._getNextPendingIndex();

    return {
      isActive: this._combatActive,
      pendingEvents: [...pendingEvents],
      consumedEvents: [...consumedEvents],
      totalEventCount: this._eventQueue.length,
      consumedCount: consumedEvents.length,
      pendingCount: pendingEvents.length,
      allConsumed: pendingEvents.length === 0,
      nextSequenceIndex: nextIndex !== -1 ? this._eventQueue[nextIndex].sequenceIndex : null,
      combatEnded: this._combatEnded,
    };
  }

  // ─── Deduplication Keys ──────────────────────────────────────────────────
  private _lastProcessedAttackKey: string = '';
  private _lastProcessedDamageKey: string = '';  // data-based key (includes HP values)
  private _lastProcessedDamageSeq: number = 0;   // monotonic counter for repeated attacks
  private _lastProcessedDeathKey: string = '';
  private _lastProcessedGoldKey: string = '';
  private _lastProcessedAbilityKey: string = '';
  private _lastProcessedResultKey: string = '';
  private _lastProcessedStreamMatchId: string | null = null; // track last processed batched stream
  private _lastProcessedStreamEventCount: number = 0;        // track last stream's event count
}
